import { useState } from "react";
import { Eye, EyeOff, RefreshCw } from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Label,
  Badge,
  Switch,
  Checkbox,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Avatar,
  AvatarFallback,
} from "@b2b/ui-components";
import { adminService } from "@b2b/services";
import { CountryPicker } from "../CountryPicker";
import { ADMIN_STAFF_MODULE_CATALOG } from "../../config/SideBarItems.config";
import { SHOW_RFQ_FEATURES } from "../../config/featureFlags";

interface AddUserModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUserCreated?: () => void;
  canManageStaff?: boolean;
}

export function AdminAddUser({
  open,
  onOpenChange,
  onUserCreated,
  canManageStaff = false,
}: AddUserModalProps) {
  const staffModuleOptions = ADMIN_STAFF_MODULE_CATALOG.filter(
    (moduleItem) => SHOW_RFQ_FEATURES || moduleItem.key !== "rfq",
  );
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    role: "",
    status: true,
    company: "",
    country: "",
    password: "",
    autoGenerate: false,
    sendNotification: true,
    allowedModules: [] as string[],
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{
    fullName?: string;
    email?: string;
    role?: string;
    company?: string;
    password?: string;
    allowedModules?: string;
  }>({});
  //   const { toast } = useToast();

  const generatePassword = () => {
    const chars =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*";
    let password = "";
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData((prev) => ({ ...prev, password }));
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFieldErrors({});

    // Validate required fields
    const nextFieldErrors: {
      fullName?: string;
      email?: string;
      role?: string;
      company?: string;
      password?: string;
      allowedModules?: string;
    } = {};

    if (!formData.fullName.trim()) {
      nextFieldErrors.fullName = "Full name is required";
    }

    if (!formData.email.trim()) {
      nextFieldErrors.email = "Email address is required";
    }

    if (!formData.role) {
      nextFieldErrors.role = "Role is required";
    }

    if (
      (formData.role === "SELLER" || formData.role === "BUYER_SELLER") &&
      !formData.company.trim()
    ) {
      nextFieldErrors.company = "Company is required";
    }

    if (!formData.password.trim()) {
      nextFieldErrors.password = "Password is required";
    }

    if (formData.role === "STAFF" && formData.allowedModules.length === 0) {
      nextFieldErrors.allowedModules = "Select at least one module for staff";
    }

    if (Object.keys(nextFieldErrors).length > 0) {
      setFieldErrors(nextFieldErrors);
      //   toast({
      //     title: "Validation Error",
      //     description: "Please fill in all required fields.",
      //     variant: "destructive",
      //   });
      setIsSubmitting(false);
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setFieldErrors((prev) => ({
        ...prev,
        email: "Please enter a valid email address",
      }));
      //   toast({
      //     title: "Invalid Email",
      //     description: "Please enter a valid email address.",
      //     variant: "destructive",
      //   });
      setIsSubmitting(false);
      return;
    }

    try {
      setError(null); // Clear any previous errors

      // Prepare user data for API call
      const userData = {
        email: formData.email,
        firstName: formData.fullName.split(" ")[0] || formData.fullName,
        lastName: formData.fullName.split(" ").slice(1).join(" ") || "",
        phone: formData.phone,
        role: formData.role as
          | "BUYER"
          | "SELLER"
          | "BUYER_SELLER"
          | "COMPANY_ADMIN"
          | "EMPLOYEE"
          | "STAFF",
        ...(formData.role === "STAFF"
          ? { allowedModules: formData.allowedModules }
          : { companyId: formData.company }),
        country: formData.country,
        businessType:
          formData.role === "SELLER" || formData.role === "BUYER_SELLER"
            ? "seller"
            : "buyer",
        password: formData.password,
      };

      console.log("Creating user with data:", userData);

      // Call the real API
      await adminService.createUser(userData);

      console.log("User created successfully");

      // Call the callback to refresh the users list
      if (onUserCreated) {
        onUserCreated();
      }

      // Close the modal
      onOpenChange(false);

      // toast({
      //   title: "User Created Successfully",
      //   description: `${formData.fullName} has been added to the system.`,
      // });
    } catch (error: any) {
      console.error("Error creating user:", error);
      setError(error.message || "Failed to create user. Please try again.");
      // toast({
      //   title: "Error",
      //   description: "Failed to create user. Please try again.",
      //   variant: "destructive",
      // });
      setIsSubmitting(false);
      return;
    }

    // Reset form
    setFormData({
      fullName: "",
      email: "",
      phone: "",
      role: "",
      status: true,
      company: "",
      country: "",
      password: "",
      autoGenerate: true,
      sendNotification: true,
      allowedModules: [],
    });
    setError(null); // Clear any errors

    setIsSubmitting(false);
    onOpenChange(false);
  };

  const handleCancel = () => {
    setFormData({
      fullName: "",
      email: "",
      phone: "",
      role: "",
      status: true,
      company: "",
      country: "",
      password: "",
      autoGenerate: true,
      sendNotification: true,
      allowedModules: [],
    });
    onOpenChange(false);
  };

  // Auto-generate password when toggle is enabled
  const handleAutoGenerateToggle = (enabled: boolean) => {
    setFormData((prev) => ({ ...prev, autoGenerate: enabled }));
    if (enabled && !formData.password) {
      generatePassword();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white"
        showCloseButton={true}
      >
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-foreground">
            Add New User
          </DialogTitle>
        </DialogHeader>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate className="space-y-6">
          {/* User Details Section */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">User Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="fullName">
                    Full Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="fullName"
                    value={formData.fullName}
                    onChange={(e) =>
                      handleInputChange("fullName", e.target.value)
                    }
                    placeholder="Enter full name"
                    required
                  />
                  {fieldErrors.fullName && (
                    <p className="text-xs text-red-500">{fieldErrors.fullName}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">
                    Email Address <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    placeholder="Enter email address"
                    required
                  />
                  {fieldErrors.email && (
                    <p className="text-xs text-red-500">{fieldErrors.email}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => handleInputChange("phone", e.target.value)}
                    placeholder="Enter phone number"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="role">
                    Role <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={formData.role}
                    onValueChange={(value) => handleInputChange("role", value)}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="BUYER">Buyer</SelectItem>
                      <SelectItem value="SELLER">Seller</SelectItem>
                      <SelectItem value="BUYER_SELLER">
                        Buyer & Seller
                      </SelectItem>
                      <SelectItem value="COMPANY_ADMIN">
                        Company Admin
                      </SelectItem>
                      <SelectItem value="EMPLOYEE">Employee</SelectItem>
                      {canManageStaff && (
                        <SelectItem value="STAFF">Staff</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                  {fieldErrors.role && (
                    <p className="text-xs text-red-500">{fieldErrors.role}</p>
                  )}
                </div>
              </div>

              {formData.role !== "STAFF" && (
              <div className="space-y-2">
                <Label htmlFor="company">
                  Company{" "}
                  {(formData.role === "SELLER" ||
                    formData.role === "BUYER_SELLER") && (
                    <span className="text-red-500">*</span>
                  )}
                </Label>
                <Input
                  id="company"
                  value={formData.company}
                  onChange={(e) => handleInputChange("company", e.target.value)}
                  placeholder="Enter company name"
                  required={
                    formData.role === "SELLER" ||
                    formData.role === "BUYER_SELLER"
                  }
                />
                {fieldErrors.company && (
                  <p className="text-xs text-red-500">{fieldErrors.company}</p>
                )}
              </div>
              )}

              {formData.role === "STAFF" && (
                <div className="space-y-2">
                  <Label>
                    Allowed modules <span className="text-red-500">*</span>
                  </Label>
                  <div className="grid grid-cols-2 gap-2 rounded-md border border-slate-200 p-3">
                    {staffModuleOptions.map((moduleItem) => {
                      const checked = formData.allowedModules.includes(
                        moduleItem.key,
                      );
                      return (
                        <label
                          key={moduleItem.key}
                          className="flex items-center gap-2 text-sm text-slate-700"
                        >
                          <Checkbox
                            checked={checked}
                            onCheckedChange={(value) => {
                              const next = value
                                ? [...formData.allowedModules, moduleItem.key]
                                : formData.allowedModules.filter(
                                    (key) => key !== moduleItem.key,
                                  );
                              handleInputChange("allowedModules", next);
                            }}
                          />
                          {moduleItem.label}
                        </label>
                      );
                    })}
                  </div>
                  {fieldErrors.allowedModules && (
                    <p className="text-xs text-red-500">
                      {fieldErrors.allowedModules}
                    </p>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="country">Country</Label>
                <CountryPicker
                  value={formData.country}
                  onValueChange={(value) => handleInputChange("country", value)}
                  placeholder="Select country"
                  triggerClassName="h-11 border-slate-200 bg-white shadow-sm hover:border-slate-300 hover:shadow-md focus-visible:ring-2 focus-visible:ring-[#c72030]/20 focus-visible:border-[#c72030] transition-all duration-200 text-slate-700"
                  popoverContentClassName="rounded-lg border-slate-200 shadow-xl"
                  commandInputWrapperClassName="h-10 items-center border-b border-slate-100 px-3"
                  commandInputClassName="h-8 py-0 text-sm placeholder:text-slate-400"
                  commandListClassName="max-h-56 overflow-y-auto py-1 pr-1"
                  commandItemClassName="mx-1 my-0.5 rounded-md px-3 py-2 text-sm text-slate-700 data-[selected=true]:bg-slate-100 data-[selected=true]:text-slate-900 hover:bg-slate-50"
                  stopListScrollPropagation
                />
              </div>
            </CardContent>
          </Card>

          {/* Account Settings Section */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Account Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <p className="text-sm text-gray-500">
                    Enable or disable user account
                  </p>
                </div>
                <Switch
                  checked={formData.status}
                  onCheckedChange={(checked) =>
                    handleInputChange("status", checked)
                  }
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="mb-1">Auto-generate Password</Label>
                  <p className="text-sm text-gray-500">
                    Automatically create a secure password
                  </p>
                </div>
                <Switch
                  checked={formData.autoGenerate}
                  onCheckedChange={handleAutoGenerateToggle}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">
                  Password <span className="text-red-500">*</span>
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) =>
                      handleInputChange("password", e.target.value)
                    }
                    placeholder="Enter password"
                    disabled={formData.autoGenerate}
                    required
                  />
                  <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center space-x-1">
                    {formData.autoGenerate && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={generatePassword}
                        className="h-8 w-8 p-0"
                      >
                        <RefreshCw className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowPassword(!showPassword)}
                      className="h-8 w-8 p-0"
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
                {fieldErrors.password && (
                  <p className="text-xs text-red-500">{fieldErrors.password}</p>
                )}
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="mb-1">Send Email Notification</Label>
                  <p className="text-sm text-gray-500">
                    Send login details to user's email
                  </p>
                </div>
                <Switch
                  checked={formData.sendNotification}
                  onCheckedChange={(checked) =>
                    handleInputChange("sendNotification", checked)
                  }
                />
              </div>
            </CardContent>
          </Card>

          <DialogFooter className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={isSubmitting}>
              {isSubmitting ? "Creating..." : "Create User"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
